// Utility functions for handling images

/**
 * Get the appropriate image URL - use proxy if needed
 * @param {string} imageUrl - The original image URL
 * @param {boolean} useProxy - Whether to use the proxy server
 * @returns {string} - The processed image URL
 */
export const getImageUrl = (imageUrl, useProxy = false) => {
  if (!imageUrl) {
    console.warn('⚠️ Empty image URL provided');
    return '';
  }
  
  // If it's already a data URL or local URL, return as-is
  if (imageUrl.startsWith('data:') || imageUrl.startsWith('/')) {
    return imageUrl;
  }
  
  // Check if we should use proxy (for external images that might have CORS issues)
  if (useProxy || shouldUseProxy(imageUrl)) {
    // Encode the URL properly
    const encodedUrl = encodeURIComponent(imageUrl);
    // Use relative path so React dev proxy works, or full URL in production
    const proxyUrl = `/api/image-proxy?url=${encodedUrl}`;
    console.log('🔄 Using proxy for:', imageUrl.substring(0, 50) + '...');
    console.log('   Proxy URL:', proxyUrl.substring(0, 80) + '...');
    return proxyUrl;
  }
  
  return imageUrl;
};

/**
 * Determine if we should use a proxy for this image
 * @param {string} imageUrl - The image URL to check
 * @returns {boolean} - Whether to use proxy
 */
const shouldUseProxy = (imageUrl) => {
  // List of domains that might have CORS issues
  const corsProblematicDomains = [
    'onlyinboards.com',
    // Add more domains as needed
  ];
  
  return corsProblematicDomains.some(domain => imageUrl.includes(domain));
};

/**
 * Preload an image to check if it loads successfully
 * @param {string} imageUrl - The image URL to preload
 * @returns {Promise<boolean>} - Whether the image loaded successfully
 */
export const preloadImage = (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = imageUrl;
  });
};

/**
 * Get a working image URL, trying direct first, then proxy
 * @param {string} imageUrl - The original image URL
 * @returns {Promise<string>} - A working image URL
 */
export const getWorkingImageUrl = async (imageUrl) => {
  if (!imageUrl) return '';
  
  // Try direct URL first
  const directWorks = await preloadImage(imageUrl);
  if (directWorks) {
    return imageUrl;
  }
  
  // If direct doesn't work, try with proxy
  console.log(`Direct loading failed for ${imageUrl}, trying proxy...`);
  return getImageUrl(imageUrl, true);
};

/**
 * Process an array of image URLs
 * @param {string[]} imageUrls - Array of image URLs
 * @param {boolean} useProxy - Whether to use proxy for all images
 * @returns {string[]} - Processed image URLs
 */
export const processImageUrls = (imageUrls, useProxy = false) => {
  if (!Array.isArray(imageUrls)) return [];
  return imageUrls.map(url => getImageUrl(url, useProxy));
};
